'use client';

import React from 'react';
import styled from 'styled-components';

interface RangeSliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  labels: Array<{ value: number; label: string }>;
  className?: string;
}

const Container = styled.div`
  width: 100%;
  margin: 24px 0;
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 40px;
  margin-bottom: 16px;
`;

const Track = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 6px;
  background: #374151;
  border-radius: 3px;
`;

const FilledTrack = styled.div<{ $percentage: number }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 6px;
  background: linear-gradient(90deg, #5B47E5 0%, #6366F1 100%);
  border-radius: 3px;
  width: ${({ $percentage }) => $percentage}%;
  transition: width 0.2s ease;
`;

const Thumb = styled.div<{ $percentage: number }>`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  left: ${({ $percentage }) => $percentage}%;
  width: 20px;
  height: 20px;
  background: #ffffff;
  border: 3px solid #5B47E5;
  border-radius: 50%;
  cursor: pointer;
  transition: left 0.2s ease;
  z-index: 2;

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 3;
`;

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const Label = styled.div<{ $active: boolean }>`
  font-size: 14px;
  color: ${({ $active }) => ($active ? '#ffffff' : '#6b7280')};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  user-select: none;
`;

/**
 * RangeSlider Component
 * 
 * Custom range slider with labeled intervals
 */
export const RangeSlider: React.FC<RangeSliderProps> = ({
  value,
  onChange,
  min,
  max,
  labels,
  className,
}) => {
  const percentage = ((value - min) / (max - min)) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value));
  };

  const getActiveLabel = () => {
    // Find the closest label to current value
    return labels.reduce((prev, curr) => {
      return Math.abs(curr.value - value) < Math.abs(prev.value - value) ? curr : prev;
    });
  };

  const activeLabel = getActiveLabel();

  return (
    <Container className={className}>
      <SliderWrapper>
        <Track />
        <FilledTrack $percentage={percentage} />
        <Thumb $percentage={percentage} />
        <HiddenInput
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={handleChange}
        />
      </SliderWrapper>
      <Labels>
        {labels.map((label) => (
          <Label key={label.value} $active={label.value === activeLabel.value}>
            {label.label}
          </Label>
        ))}
      </Labels>
    </Container>
  );
};

export default RangeSlider;

